'use client';

import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SubscriberStatus = 'active' | 'unsubscribed' | 'bounced' | 'pending';
export type CampaignStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'archived';

export interface MailList {
  id: string;
  ownerId: string;
  name: string;
  description: string;
  color: string;
  createdAt: string;
}

export interface Subscriber {
  id: string;
  ownerId: string;
  mailListId: string | null;
  email: string;
  firstName: string;
  lastName: string;
  listName: string;
  status: SubscriberStatus;
  tags: string[];
  source: string;
  joinedAt: string;
  unsubscribedAt: string | null;
}

export interface Campaign {
  id: string;
  ownerId: string;
  mailListId: string | null;
  name: string;
  subject: string;
  fromName: string;
  fromEmail: string;
  bodyHtml: string;
  listName: string;
  status: CampaignStatus;
  sendTiming: 'now' | 'scheduled';
  scheduledAt: string | null;
  sentCount: number;
  totalCount: number;
  openRate: string;
  ctr: string;
  tags: string[];
  createdAt: string;
}

export interface Unsubscribe {
  id: string;
  ownerId: string;
  subscriberId: string | null;
  email: string;
  reason: string;
  campaignId: string | null;
  campaignName?: string;
  unsubscribedAt: string;
  ipAddress: string;
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    return /relation.*does not exist|column.*does not exist|function.*does not exist|syntax error|type.*does not exist/i.test(error.message);
  }
  return false;
}

// ─── Mail Lists ───────────────────────────────────────────────────────────────

export const mailListService = {
  async getAll(): Promise<MailList[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];
    const { data, error } = await supabase
      .from('mail_lists')
      .select('*')
      .eq('owner_id', user.id)
      .order('created_at', { ascending: true });
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return (data || []).map((r) => ({
      id: r.id,
      ownerId: r.owner_id,
      name: r.name,
      description: r.description || '',
      color: r.color || '#5b35e8',
      createdAt: r.created_at,
    }));
  },

  async create(list: { name: string; description: string; color: string }): Promise<MailList | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const { data, error } = await supabase
      .from('mail_lists')
      .insert({ owner_id: user.id, name: list.name, description: list.description, color: list.color })
      .select()
      .single();
    if (error) {
      if (isSchemaError(error)) throw error;
      return null;
    }
    return { id: data.id, ownerId: data.owner_id, name: data.name, description: data.description || '', color: data.color || '#5b35e8', createdAt: data.created_at };
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const { error } = await supabase.from('mail_lists').delete().eq('id', id).eq('owner_id', user.id);
    if (error) { if (isSchemaError(error)) throw error; return false; }
    return true;
  },
};

// ─── Subscribers ──────────────────────────────────────────────────────────────

export const subscriberService = {
  async getAll(): Promise<Subscriber[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];
    const { data, error } = await supabase
      .from('subscribers')
      .select('*')
      .eq('owner_id', user.id)
      .order('joined_at', { ascending: false });
    if (error) { if (isSchemaError(error)) throw error; return []; }
    return (data || []).map(mapSubscriber);
  },

  async create(sub: { email: string; firstName: string; lastName: string; listName: string; mailListId?: string }): Promise<Subscriber | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const { data, error } = await supabase
      .from('subscribers')
      .insert({
        owner_id: user.id,
        email: sub.email,
        first_name: sub.firstName,
        last_name: sub.lastName,
        list_name: sub.listName,
        mail_list_id: sub.mailListId || null,
        status: 'pending',
        source: 'Manual Add',
      })
      .select()
      .single();
    if (error) { if (isSchemaError(error)) throw error; return null; }
    return mapSubscriber(data);
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const { error } = await supabase.from('subscribers').delete().eq('id', id).eq('owner_id', user.id);
    if (error) { if (isSchemaError(error)) throw error; return false; }
    return true;
  },

  async bulkDelete(ids: string[]): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const { error } = await supabase.from('subscribers').delete().in('id', ids).eq('owner_id', user.id);
    if (error) { if (isSchemaError(error)) throw error; return false; }
    return true;
  },
};

function mapSubscriber(r: any): Subscriber {
  return {
    id: r.id,
    ownerId: r.owner_id,
    mailListId: r.mail_list_id,
    email: r.email,
    firstName: r.first_name || '',
    lastName: r.last_name || '',
    listName: r.list_name || 'All Subscribers',
    status: r.status as SubscriberStatus,
    tags: r.tags || [],
    source: r.source || '',
    joinedAt: r.joined_at,
    unsubscribedAt: r.unsubscribed_at || null,
  };
}

// ─── Campaigns ────────────────────────────────────────────────────────────────

export const campaignService = {
  async getAll(): Promise<Campaign[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];
    const { data, error } = await supabase
      .from('campaigns')
      .select('*')
      .eq('owner_id', user.id)
      .order('created_at', { ascending: false });
    if (error) { if (isSchemaError(error)) throw error; return []; }
    return (data || []).map(mapCampaign);
  },

  async create(c: {
    name: string; subject: string; fromName: string; fromEmail: string;
    bodyHtml: string; listName: string; mailListId?: string;
    sendTiming: 'now' | 'scheduled'; scheduledAt?: string; tags: string[];
  }): Promise<Campaign | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const { data, error } = await supabase
      .from('campaigns')
      .insert({
        owner_id: user.id,
        name: c.name,
        subject: c.subject,
        from_name: c.fromName,
        from_email: c.fromEmail,
        body_html: c.bodyHtml,
        list_name: c.listName,
        mail_list_id: c.mailListId || null,
        status: c.sendTiming === 'now' ? 'sending' : 'scheduled',
        send_timing: c.sendTiming,
        scheduled_at: c.scheduledAt || null,
        tags: c.tags,
      })
      .select()
      .single();
    if (error) { if (isSchemaError(error)) throw error; return null; }
    return mapCampaign(data);
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const { error } = await supabase.from('campaigns').delete().eq('id', id).eq('owner_id', user.id);
    if (error) { if (isSchemaError(error)) throw error; return false; }
    return true;
  },

  async duplicate(id: string): Promise<Campaign | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const { data: orig, error: fetchErr } = await supabase.from('campaigns').select('*').eq('id', id).single();
    if (fetchErr || !orig) return null;
    const { data, error } = await supabase
      .from('campaigns')
      .insert({
        owner_id: user.id,
        name: `${orig.name} (Copy)`,
        subject: orig.subject,
        from_name: orig.from_name,
        from_email: orig.from_email,
        body_html: orig.body_html,
        list_name: orig.list_name,
        mail_list_id: orig.mail_list_id,
        status: 'draft',
        send_timing: 'now',
        tags: orig.tags || [],
      })
      .select()
      .single();
    if (error) { if (isSchemaError(error)) throw error; return null; }
    return mapCampaign(data);
  },
};

function mapCampaign(r: any): Campaign {
  return {
    id: r.id,
    ownerId: r.owner_id,
    mailListId: r.mail_list_id,
    name: r.name,
    subject: r.subject,
    fromName: r.from_name || '',
    fromEmail: r.from_email || '',
    bodyHtml: r.body_html || '',
    listName: r.list_name || '',
    status: r.status as CampaignStatus,
    sendTiming: r.send_timing || 'now',
    scheduledAt: r.scheduled_at || null,
    sentCount: r.sent_count || 0,
    totalCount: r.total_count || 0,
    openRate: r.open_rate || '—',
    ctr: r.ctr || '—',
    tags: r.tags || [],
    createdAt: r.created_at,
  };
}

// ─── Unsubscribes ─────────────────────────────────────────────────────────────

export const unsubscribeService = {
  async getAll(): Promise<Unsubscribe[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];
    const { data, error } = await supabase
      .from('unsubscribes')
      .select('*, campaigns(name)')
      .eq('owner_id', user.id)
      .order('unsubscribed_at', { ascending: false });
    if (error) { if (isSchemaError(error)) throw error; return []; }
    return (data || []).map((r) => ({
      id: r.id,
      ownerId: r.owner_id,
      subscriberId: r.subscriber_id,
      email: r.email,
      reason: r.reason || '',
      campaignId: r.campaign_id,
      campaignName: r.campaigns?.name || '—',
      unsubscribedAt: r.unsubscribed_at,
      ipAddress: r.ip_address || '',
    }));
  },

  async resubscribe(id: string, email: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    // Remove from unsubscribes
    const { error: delErr } = await supabase.from('unsubscribes').delete().eq('id', id).eq('owner_id', user.id);
    if (delErr) { if (isSchemaError(delErr)) throw delErr; return false; }
    // Update subscriber status back to active
    await supabase.from('subscribers').update({ status: 'active', unsubscribed_at: null }).eq('email', email).eq('owner_id', user.id);
    return true;
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const { error } = await supabase.from('unsubscribes').delete().eq('id', id).eq('owner_id', user.id);
    if (error) { if (isSchemaError(error)) throw error; return false; }
    return true;
  },

  async getStats(): Promise<{ total: number; thisMonth: number; lastMonth: number }> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { total: 0, thisMonth: 0, lastMonth: 0 };
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0).toISOString();

    const [totalRes, thisMonthRes, lastMonthRes] = await Promise.all([
      supabase.from('unsubscribes').select('*', { count: 'exact', head: true }).eq('owner_id', user.id),
      supabase.from('unsubscribes').select('*', { count: 'exact', head: true }).eq('owner_id', user.id).gte('unsubscribed_at', startOfMonth),
      supabase.from('unsubscribes').select('*', { count: 'exact', head: true }).eq('owner_id', user.id).gte('unsubscribed_at', startOfLastMonth).lte('unsubscribed_at', endOfLastMonth),
    ]);

    return {
      total: totalRes.count || 0,
      thisMonth: thisMonthRes.count || 0,
      lastMonth: lastMonthRes.count || 0,
    };
  },
};
